// vim: set fdm=marker :
// {{{ import
import { useRef, useEffect, useReducer, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { parseISO, endOfWeek, format } from 'date-fns';
import * as Sentry from '@sentry/browser';

import eventEmitter from '@chronos/utils/eventEmitter';
import * as timeUtil from '@chronos/time-util';
import chronosApi from '@chronos/api-client/chronosApi';
import { setState } from '../../actions';
import keyValueReducer from '../../reducers/keyValueReducer';
// }}}

const itemsPerPage = 40;
// {{{ initialState{}
const initialState = {
  isLoading: true,
  allWorklogsLoaded: false,
  isInitialized: true,
  isPageLoading: {},
  isPageLoaded: {},

  worklogs: {},

  // Merged sorted map of live and fetched worklogs
  // key is index in the list, value is id of worklog or live worklog object
  listItems: {},
  dummyItemIndex: null,

  // sorted map of fetched worklogs, excluding live worklogs
  // key is index in the list, value is id of worklog
  worklogsSortedMap: {},
  worklogsByCardSortedMap: {},

  // Default non zero count needs to display placeholders
  worklogsCount: itemsPerPage,
  listItemsCount: itemsPerPage,
  totalByCardsCount: itemsPerPage,
  liveWorklogsCount: 0,

  totalTime: 0,
  totalTimeStr: null,
  dayPoints: {},
  weekPoints: {},
  userSeconds: {},
};
// }}}

const useWorklogsList = ({
  memberId,
  cardId,
  requestParams,
  filter,
  usersEntities,
  fetchOutTrelloMembers,
  cardsEntities,
  fetchOutTrelloCards,
  liveWorklogs,
  showLiveWorklogs,
  showOwnLiveWorklogs,
  allowedToSeeOthersWorklog,
  defaultWorklogsCount,
  weekStartsOn,
  initialTotalTime = 0,
  initialTotalTimeStr = null,
}) => {
  const [state, dispatch] = useReducer(keyValueReducer, {
    ...initialState,
    totalTime: initialTotalTime,
    totalTimeStr: initialTotalTimeStr,
    worklogsCount: defaultWorklogsCount,
    totalByCardsCount: defaultWorklogsCount,
    listItemsCount: defaultWorklogsCount,
  });
  const listRef = useRef(null);
  const infiniteLoaderRef = useRef(null);
  const autosizerRef = useRef(null);
  const isFirstRunForFiltersCheck = useRef(true);

  // {{{ 🔋 reloadList
  function reloadList() {
    log.debug('🔋', 'reloadList');
    dispatch(
      setState({
        isPageLoaded: {},
      })
    );
    if (listRef.current?.resetAfterIndex) {
      listRef.current.resetAfterIndex(0, true);
    }
    setTimeout(() => {
      if (infiniteLoaderRef.current) {
        log.debug('resetloadMoreItemsCache');
        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
      }
    }, 100);
  }
  // }}}

  // {{{ 🚿 clear list
  function clearList() {
    dispatch(
      setState({
        worklogsSortedMap: {},
        worklogsByCardSortedMap: {},
      })
    );
    reloadList();
  }
  // }}}

  // {{{ 🩸 fetchWorklogs
  const fetchWorklogs = useDebouncedCallback(
    useCallback(
      async (
        startIndex = 0,
        stopIndex = itemsPerPage,
        clearOldWorklogs = false
      ) => {
        log.debug('[fetchWorklogs]:', 'run attempt');
        const pureStartIndex = state.liveWorklogsCount
          ? startIndex + 1 - state.liveWorklogsCount
          : startIndex;
        const pureStopIndex = state.liveWorklogsCount
          ? stopIndex + 1 - state.liveWorklogsCount
          : stopIndex;
        log.debug('[fetchWorklogs]:', {
          startIndex,
          stopIndex,
          pureStartIndex,
          pureStopIndex,
          liveWorklogsCount: state.liveWorklogsCount,
          clearOldWorklogs,
        });
        const startPage = Math.floor(pureStartIndex / itemsPerPage);
        const stopPage = Math.floor(pureStopIndex / itemsPerPage);
        const shouldSkip =
          state.isPageLoading[startPage] === true ||
          state.isPageLoading[stopPage] === true ||
          (state.isPageLoaded[startPage] === true &&
            state.isPageLoaded[stopPage] === true);

        if (shouldSkip) {
          log.debug('[fetchWorklogs]:', 'skip', {
            startPage,
            stopPage,
          });
          return null;
        }

        try {
          log.debug('[fetchWorklogs]:', '🩸', 'page fetching...', {
            startPage,
            stopPage,
          });
          dispatch(
            setState({
              isPageLoading: {
                [startPage]: true,
                [stopPage]: true,
                _merge: true,
              },
            })
          );
          const isSamePage = startPage === stopPage;
          // If a list view see a border of two pages limit should be extended to fetch 2 pages
          const queryLimit = isSamePage ? itemsPerPage : itemsPerPage * 2;
          const offset = startPage * itemsPerPage;
          const { worklogs: fetchedWorklogs, total: worklogsCount } =
            await chronosApi.getWorklogs({
              body: {
                ...requestParams,
                limit: queryLimit,
                offset,
              },
            });

          if (requestParams.groupWorklogsBy === 'card') {
            const { worklogsByCardSortedMap, worklogs, outUsers, outCards } =
              fetchedWorklogs.reduce(
                (acc, w, index) => {
                  const worklogGroupStartedAt = parseISO(
                    w.worklogs[0].startedAt
                  );
                  const worklogGroupWeek = endOfWeek(worklogGroupStartedAt, {
                    weekStartsOn,
                  });
                  const worklogGroup = {
                    ...w,
                    id: `${w.day}_${w.cardId}`,
                    isWorklogGroup: true,
                    isLive: false,
                    computed: {
                      trackedTime: timeUtil.stj(
                        w.worklogs.reduce(
                          (totalSeconds, worklog) =>
                            totalSeconds + worklog.seconds,
                          0
                        )
                      ),
                      timePoints: timeUtil.getWorklogGroupTimePoints({
                        worklogs: w.worklogs,
                      }),
                      startedAt: worklogGroupStartedAt,
                      startedAtDayStr: format(worklogGroupStartedAt, 'y-MM-dd'),
                      endOfWeekDayStr: format(worklogGroupWeek, 'y-MM-dd'),
                    },
                  };
                  worklogGroup.worklogs = worklogGroup.worklogs.map(
                    (worklog) => {
                      const trackedTime = timeUtil.stj(
                        worklog.seconds || 0,
                        true
                      );
                      const startedAt = parseISO(worklog.startedAt);
                      const timePoints = timeUtil.getWorklogTimePoints({
                        from: startedAt,
                        seconds: worklog.seconds,
                        isLive: false,
                      });
                      const week = endOfWeek(startedAt, {
                        weekStartsOn,
                      });
                      worklog.computed = {
                        timePoints,
                        trackedTime,
                        startedAt,
                        startedAtDayStr: format(startedAt, 'y-MM-dd'),
                        endOfWeekDayStr: format(week, 'y-MM-dd'),
                      };

                      acc.worklogs[worklog.id] = worklog;

                      const user = usersEntities[worklog.accountId];
                      const card = cardsEntities[worklog.cardId];

                      if (!user) {
                        acc.outUsers[worklog.accountId] = true;
                      }
                      if (!card) {
                        acc.outCards[worklog.cardId] = true;
                      }
                      return worklog.id;
                    }
                  );

                  acc.worklogsByCardSortedMap[index + offset] = worklogGroup;
                  return acc;
                },
                {
                  // Sorted ids by the backend
                  worklogsByCardSortedMap: [],
                  worklogs: {},
                  outUsers: {},
                  outCards: {},
                }
              );

            fetchOutTrelloCards(Object.keys(outCards));
            fetchOutTrelloMembers(Object.keys(outUsers));

            dispatch(
              setState({
                worklogs: {
                  ...(clearOldWorklogs ? {} : state.worklogs),
                  ...worklogs,
                },
                worklogsByCardSortedMap: clearOldWorklogs
                  ? worklogsByCardSortedMap
                  : {
                      ...state.worklogsByCardSortedMap,
                      ...worklogsByCardSortedMap,
                    },
                totalByCardsCount: worklogsCount,
                isPageLoading: {
                  [startPage]: false,
                  [stopPage]: false,
                  _merge: true,
                },
                isPageLoaded: {
                  [startPage]: true,
                  [stopPage]: true,
                  _merge: true,
                },
                dummyItemIndex: null,
              })
            );
          }

          if (!requestParams.groupWorklogsBy) {
            const { worklogsSortedMap, worklogs, outUsers, outCards } =
              fetchedWorklogs.reduce(
                (acc, w, index) => {
                  const worklog = {
                    ...w,
                    isLive: false,
                  };
                  const trackedTime = timeUtil.stj(worklog.seconds || 0, true);
                  const startedAt = parseISO(worklog.startedAt);
                  const timePoints = timeUtil.getWorklogTimePoints({
                    from: startedAt,
                    seconds: worklog.seconds,
                  });
                  const week = endOfWeek(startedAt, {
                    weekStartsOn,
                  });
                  worklog.computed = {
                    timePoints,
                    trackedTime,
                    startedAt,
                    startedAtDayStr: format(startedAt, 'y-MM-dd'),
                    endOfWeekDayStr: format(week, 'y-MM-dd'),
                  };

                  acc.worklogs[worklog.id] = worklog;
                  acc.worklogsSortedMap[index + offset] = worklog.id;

                  const user = usersEntities[worklog.accountId];
                  const card = cardsEntities[worklog.cardId];

                  if (!user) {
                    acc.outUsers[worklog.accountId] = true;
                  }
                  if (!card) {
                    acc.outCards[worklog.cardId] = true;
                  }
                  return acc;
                },
                {
                  // Sorted ids by the backend
                  worklogsSortedMap: {},
                  worklogs: {},
                  outUsers: {},
                  outCards: {},
                }
              );

            fetchOutTrelloCards(Object.keys(outCards));
            fetchOutTrelloMembers(Object.keys(outUsers));

            dispatch(
              setState({
                worklogs: {
                  ...(clearOldWorklogs ? {} : state.worklogs),
                  ...worklogs,
                },
                worklogsSortedMap: clearOldWorklogs
                  ? worklogsSortedMap
                  : { ...state.worklogsSortedMap, ...worklogsSortedMap },
                worklogsCount,
                isPageLoading: {
                  [startPage]: false,
                  [stopPage]: false,
                  _merge: true,
                },
                isPageLoaded: {
                  [startPage]: true,
                  [stopPage]: true,
                  _merge: true,
                },
                dummyItemIndex: null,
              })
            );
          }
        } catch (err) {
          log.error(err);
          Sentry.captureException(err);
          if (err?.response?.status === 403) {
            dispatch(
              setState({
                isLicenseExpired: err.response.data.isLicenseExpired,
                isUserActivate: err.response.data.isUserActivate,
              })
            );
          }
          dispatch(
            setState({
              isPageLoading: {
                [startPage]: false,
                [stopPage]: false,
                _merge: true,
              },
              isPageLoaded: {
                [startPage]: false,
                [stopPage]: false,
                _merge: true,
              },
            })
          );
        }
        return null;
      },
      [
        state.worklogs,
        usersEntities,
        state.isPageLoading,
        state.isPageLoaded,
        state.worklogsSortedMap,
        state.worklogsByCardSortedMap,
        state.liveWorklogsCount,
        requestParams,
      ]
    ),
    1000,
    {
      leading: true,
    }
  );
  // }}}

  // {{{  👁 fetchWorklogsMeta
  const fetchWorklogsMeta = useCallback(
    async (params) => {
      const { shouldMarkAsLoading = false, shouldReloadListIfChange = true } =
        params ?? {};
      log.debug('👁', '[uwl]:[fwm]', {
        shouldMarkAsLoading,
        shouldReloadListIfChange,
      });
      if (shouldMarkAsLoading) {
        dispatch(
          setState({
            isLoading: true,
          })
        );
      }
      let totalTime = 0;

      try {
        const {
          totalTime: totalWorklogsTime,
          totalByCardsCount,
          totalWorklogs,
          dayPoints,
          weekPoints,
          userSeconds,
          hasScreenshots,
        } = await chronosApi.getWorklogsMeta({
          body: requestParams,
        });
        totalTime = totalWorklogsTime;
        const currentTotalWorklogs = state.worklogsCount;

        const isTotalTimeChanged =
          state.totalTime === 0 || state.totalTime !== totalTime;
        const isTotalWorklogsCountChanged =
          currentTotalWorklogs === 0 || currentTotalWorklogs !== totalWorklogs;

        if (
          shouldReloadListIfChange &&
          (isTotalTimeChanged || isTotalWorklogsCountChanged)
        ) {
          log.debug('👁', '[uwl]:[fwm]', 'reloadList(🔋)', {
            isTotalTimeChanged,
            isTotalWorklogsCountChanged,
          });
          reloadList();
        } else {
          log.debug('[uwl]:[fwm]', 'skip reloadList', {
            isTotalTimeChanged,
            isTotalWorklogsCountChanged,
          });
        }

        fetchOutTrelloMembers(
          Object.keys(userSeconds).filter((userId) => !usersEntities[userId])
        );

        dispatch(
          setState({
            allWorklogsLoaded: !isTotalWorklogsCountChanged,
            isScreenshotsExists: hasScreenshots,
            dayPoints,
            weekPoints,
            userSeconds: {
              ...userSeconds,
              _merge: filter.members.selected.length > 0,
            },
            totalTime,
            totalTimeStr: totalTime ? timeUtil.stj(totalTime) : null,
            worklogsCount: totalWorklogs,
            ...(totalByCardsCount !== undefined ? { totalByCardsCount } : {}),
          })
        );
      } catch (err) {
        log.error(err);
        Sentry.captureException(err);
        if (err?.response?.status === 403) {
          dispatch(
            setState({
              isLicenseExpired: err.response.data.isLicenseExpired,
              isUserActivate: err.response.data.isUserActivate,
            })
          );
        }
      }
      dispatch(
        setState({
          isLoading: false,
        })
      );
      return {
        totalTime,
      };
    },
    [
      requestParams,
      infiniteLoaderRef.current,
      usersEntities,
      state.worklogsCount,
      state.totalTime,
      state.boardSettings,
    ]
  );
  // }}}

  // {{{ ♻️  reFetchWorklog
  const reFetchWorklog = useCallback(async ({ worklogId, index }) => {
    log.debug('[reFetchWorklog]:', { worklogId, index });
    const { worklogs: fetchedWorklogs } = await chronosApi.getWorklogs({
      body: {
        worklogIds: [worklogId],
      },
    });
    if (fetchedWorklogs.length) {
      const worklog = {
        ...fetchedWorklogs[0],
        isLive: false,
      };
      const trackedTime = timeUtil.stj(worklog.seconds || 0, true);
      const startedAt = parseISO(worklog.startedAt);
      const timePoints = timeUtil.getWorklogTimePoints({
        from: parseISO(worklog.startedAt),
        seconds: worklog.seconds,
      });
      const week = endOfWeek(startedAt, {
        weekStartsOn,
      });
      worklog.computed = {
        timePoints,
        trackedTime,
        startedAt,
        startedAtDayStr: format(startedAt, 'y-MM-dd'),
        endOfWeekDayStr: format(week, 'y-MM-dd'),
      };
      dispatch(
        setState({
          worklogs: {
            [worklog.id]: worklog,
            _merge: true,
          },
        })
      );
    }
  }, []);
  // }}}

  // {{{ deleteWorklog
  const deleteWorklog = useCallback(
    async (worklog, index, groupIndex) => {
      if (groupIndex) {
        const { [groupIndex]: worklogGroup } = state.worklogsByCardSortedMap;
        if (worklogGroup) {
          worklogGroup.worklogs = worklogGroup.worklogs.filter(
            (id) => id !== worklog.id
          );
          if (worklogGroup.worklogs.length) {
            dispatch(
              setState({
                worklogsByCardSortedMap: {
                  ...state.worklogsByCardSortedMap,
                  [groupIndex]: worklogGroup,
                },
              })
            );
          } else {
            const { [groupIndex]: removedGroup, ...worklogsByCardSortedMap } =
              state.worklogsByCardSortedMap;
            dispatch(
              setState({
                worklogsByCardSortedMap,
              })
            );
          }
        }
        const {
          [index - state.liveWorklogsCount]: removedValue,
          ...worklogsByCardSortedMap
        } = state.worklogsByCardSortedMap;
        dispatch(
          setState({
            worklogsByCardSortedMap,
          })
        );
      } else {
        const { [index]: removedValue, ...worklogsSortedMap } =
          state.worklogsSortedMap;
        dispatch(
          setState({
            worklogsSortedMap,
          })
        );
      }

      await chronosApi.deleteWorklog({
        body: {
          worklogId: worklog.id,
        },
      });
    },
    [state.worklogsSortedMap, state.worklogsByCardSortedMap]
  );
  // }}}

  // {{{ addNewDummyWorklog
  const addNewDummyWorklog = useCallback(() => {
    dispatch(
      setState({
        dummyItemIndex: 0,
      })
    );
  }, []);
  // }}}

  const hotUpdate = useCallback(
    ({ action, worklog }) => {
      log.debug('possible to make hot update without fetch', {
        action,
        worklog,
      });
      fetchWorklogsMeta();
    },
    [fetchWorklogsMeta]
  );

  useEffect(() => {
    eventEmitter.on('worklog', hotUpdate);
    return () => {
      eventEmitter.off('worklog', hotUpdate);
    };
  }, [hotUpdate]);

  // {{{ onResize
  /* reset after resize */
  // {{
  const resetAfterResize = useDebouncedCallback(
    () => {
      if (listRef.current?.resetAfterIndex) {
        listRef.current.resetAfterIndex(0, true);
      }
      /* eslint-disable */
      if (autosizerRef.current?._onResize) {
        autosizerRef.current?._onResize();
      }
      /* eslint-enable */
    },
    100,
    {
      leading: true,
    }
  );
  useEffect(() => {
    window.addEventListener('resize', resetAfterResize);
    return () => {
      window.removeEventListener('resize', resetAfterResize);
    };
  }, []);
  // }}}

  // {{{ 🕹️ onChangeFilterEffect
  const onChangeFilterEffect = useDebouncedCallback(
    async () => {
      if (!isFirstRunForFiltersCheck.current) {
        const reloadState = {
          //  worklogsCount: itemsPerPage + 5,
          //  listItemsCount: itemsPerPage + 5,
          worklogsSortedMap: {},
          worklogsByCardSortedMap: {},
          // isPageLoaded: {},
        };
        dispatch(setState(reloadState));

        log.debug('🕹️', '[uwl]:[ocf]:', 'reload!', reloadState);
        log.debug('🕹️', '[uwl]:[ocf]:', 'run fetchWorklogsMeta(👁)');
        await fetchWorklogsMeta({
          shouldMarkAsLoading: false,
          shouldReloadListIfChange: false,
        });
        log.debug('🕹️', '[uwl]:[ocf]:', 'run reloadList(🔋)');
        reloadList();
      }
      isFirstRunForFiltersCheck.current = false;
    },
    1000,
    {
      leading: true,
    }
  );
  useEffect(() => {
    onChangeFilterEffect();
  }, [requestParams]);
  // }}}

  // {{{ Check live worklogs for out cards and users
  useEffect(() => {
    fetchOutTrelloCards(
      Object.keys(
        liveWorklogs.reduce((acc, w) => {
          const card = cardsEntities[w.cardId];
          if (card === undefined || card === null) {
            acc[w.cardId] = w.cardId;
          }
          return acc;
        }, {})
      )
    );

    fetchOutTrelloMembers(
      Object.keys(
        liveWorklogs.reduce((acc, w) => {
          const user = usersEntities[w.memberId];
          if (user === undefined || user === null) {
            acc[w.memberId] = w.memberId;
          }
          return acc;
        }, {})
      )
    );
  }, [liveWorklogs]);
  // }}}

  // {{{ 🧠 ComputeList effect
  // TODO: useMemo?
  useEffect(() => {
    if (state.isInitialized) {
      log.debug('🧠', '[cle]:', 'run computation', {
        isInitialized: state.isInitialized,
        count: liveWorklogs.length,
        liveWorklogs,
        showLiveWorklogs,
      });

      // live worklogs filter
      const liveWorklogsFiltered = (
        showLiveWorklogs
          ? liveWorklogs.filter((w) => {
              if (allowedToSeeOthersWorklog) {
                return showOwnLiveWorklogs ? true : w.memberId !== memberId;
              }
              if (showOwnLiveWorklogs) {
                return w.memberId === memberId;
              }
              return false;
            })
          : []
      ).filter((w) => {
        const card = cardsEntities[w.cardId];
        if (!card || card?.isUnknown) {
          return false;
        }
        let isPass = true;
        if (filter.members.selected.length) {
          isPass = filter.members.selected.includes(w.memberId);
        }
        if (filter.cards.selected.length && isPass) {
          isPass = filter.cards.selected.includes(w.cardId);
        }
        if (cardId) {
          isPass = w.cardId === cardId;
        }
        if (isPass) {
          const from = new Date(w.startTime);
          return filter.date.ranges[0].endDate >= from;
        }
        return isPass;
      });
      const liveWorklogsCount = liveWorklogsFiltered.length;
      log.debug('[liveWorklogs]', 'effect', 'after filter', {
        liveWorklogsCount,
        liveWorklogs,
        liveWorklogsFiltered,
        filter,
      });
      const liveWorklogsItems = liveWorklogsFiltered.reduce(
        (acc, liveWorklog, index) => {
          const startedAt = new Date(liveWorklog.startTime);
          const week = endOfWeek(startedAt, {
            weekStartsOn,
          });
          const timePoints = timeUtil.getWorklogTimePoints({
            from: startedAt,
            seconds: 0, // it doesnt' matter here
          });

          acc[index] = {
            ...liveWorklog,
            accountId: liveWorklog.memberId,
            comment: liveWorklog.trackingWorklogComment,
            type: 'auto',
            isLive: true,
            isOwn: liveWorklog.memberId === memberId,
            startedAt: startedAt.toISOString(),
            updatedAt: null,
            computed: {
              timePoints,
              startedAt,
              startedAtDayStr: format(startedAt, 'y-MM-dd'),
              endOfWeekDayStr: format(week, 'y-MM-dd'),
            },
            timerState: liveWorklog,
          };
          return acc;
        },
        {}
      );

      let listItems = {};

      if (filter.groupWorklogsBy === 'card') {
        listItems = Object.keys(state.worklogsByCardSortedMap).reduce(
          (acc, indexStr) => {
            const index = parseInt(indexStr, 10) + liveWorklogsCount;
            acc[index] = state.worklogsByCardSortedMap[indexStr];
            return acc;
          },
          {}
        );
      }

      if (!filter.groupWorklogsBy) {
        listItems = Object.keys(state.worklogsSortedMap).reduce(
          (acc, indexStr) => {
            const worklogId = state.worklogsSortedMap[indexStr];
            let index = parseInt(indexStr, 10) + liveWorklogsCount;
            if (
              state.dummyItemIndex !== null &&
              index >= state.dummyItemIndex
            ) {
              index += 1;
            }
            acc[index] = worklogId;
            return acc;
          },
          state.dummyItemIndex === null
            ? {}
            : { [state.dummyItemIndex + liveWorklogsCount]: null }
        );
      }

      let listItemsCount = state.worklogsCount + liveWorklogsCount;
      if (filter.groupWorklogsBy === 'card') {
        listItemsCount = state.totalByCardsCount + liveWorklogsCount;
      }

      dispatch(
        setState({
          listItems: {
            ...liveWorklogsItems,
            ...listItems,
          },
          listItemsCount,
          liveWorklogsCount,
        })
      );
      if (listRef.current?.resetAfterIndex) {
        listRef.current.resetAfterIndex(0, true);
      }
    } else {
      log.debug('[cle]:', 'skip', {
        isInitialized: state.isInitialized,
      });
    }
  }, [
    liveWorklogs,
    showLiveWorklogs,
    showOwnLiveWorklogs,
    allowedToSeeOthersWorklog,
    state.dummyItemIndex,
    state.isInitialized,
    state.boardSettings,
    filter,
    cardId,
    cardsEntities,
    state.worklogs,
    state.worklogsSortedMap,
    state.worklogsByCardSortedMap,
    state.worklogsCount,
    state.totalByCardsCount,
  ]);
  // }}}

  // {{{ isItemLoaded
  const isItemLoaded = (index) => {
    let isLoaded = !state.isLoading;

    if (isLoaded && index >= state.liveWorklogsCount - 1) {
      const pureIndex = state.liveWorklogsCount
        ? index + 1 - state.liveWorklogsCount
        : index;
      const page = Math.floor(pureIndex / itemsPerPage);
      isLoaded = state.isPageLoaded[page] === true;
    }
    return isLoaded;
  };
  // }}}

  return [
    {
      state,
      listRef,
      infiniteLoaderRef,
      autosizerRef,
    },
    {
      dispatch,
      fetchWorklogs,
      fetchWorklogsMeta,
      reloadList,
      clearList,
      isItemLoaded,
      addNewDummyWorklog,
      deleteWorklog,
      reFetchWorklog,
    },
  ];
};

export default useWorklogsList;
